import React from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { getBrandInitial } from '../../Utils/brandForm';
import { resolveBrandColor } from '../../Map/utils/sharedBranding';
import { colors } from '../../styles/theme';
import {
  brandsCardMetaSx,
  brandsCardNameSx,
  brandsCardSx,
  brandsDefaultChipSx,
  brandsEmptyStateSx,
  brandsHelperTextSx,
  brandsIconButtonSx,
  brandsListWellSx,
  brandsLogoBoxSx,
  brandsLogoImageSx,
  brandsPrimaryButtonSx,
  brandsTextColors,
  brandsTooltipSlotProps,
} from './BrandsModal.styles';

/** Email o teléfono: lo que la marca aporta a la página compartida. */
function buildBrandMeta(brand) {
  const parts = [];
  if (brand.email) parts.push(brand.email);
  else if (brand.phone) parts.push(brand.phone);
  return parts.join(' · ');
}

function BrandAvatar({ brand }) {
  const accent = resolveBrandColor(brand.primary_color, colors.primary);
  return (
    <Box sx={brandsLogoBoxSx}>
      {brand.logo_url ? (
        <Box component="img" src={brand.logo_url} alt="" sx={brandsLogoImageSx} />
      ) : (
        <Typography sx={{ color: accent, fontSize: '18px', fontWeight: 700, lineHeight: 1 }}>
          {getBrandInitial(brand.brand_name)}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Índice de identidades del usuario. `can_manage` es siempre true (son las
 * tuyas); se respeta por si el GET trae una fila vieja.
 *
 * @param {{
 *   brands: object[],
 *   canCreateBrand: boolean,
 *   busyBrandId?: string,
 *   onCreate: () => void,
 *   onEdit: (brand: object) => void,
 *   onDelete: (brand: object) => void,
 *   onSetDefault: (brand: object) => void,
 * }} props
 */
export default function BrandList({
  brands,
  canCreateBrand,
  busyBrandId = '',
  onCreate,
  onEdit,
  onDelete,
  onSetDefault,
}) {
  if (brands.length === 0) {
    return (
      <Box sx={brandsListWellSx}>
        <Box sx={brandsEmptyStateSx}>
          <PaletteOutlinedIcon sx={{ fontSize: 34, color: brandsTextColors.muted }} />
          <Typography sx={{ color: brandsTextColors.primary, fontSize: '14px', fontWeight: 600 }}>
            Aún no creaste ninguna identidad
          </Typography>
          <Typography sx={{ ...brandsHelperTextSx, maxWidth: 380 }}>
            Con una identidad cargada podés compartir listas y lotes con tu logo,
            tus colores y tus datos de contacto en lugar de la vista por defecto de Terrio.
          </Typography>
          {canCreateBrand ? (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={onCreate}
              sx={{ ...brandsPrimaryButtonSx, mt: 1 }}
            >
              Crear identidad
            </Button>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={brandsListWellSx}>
      {brands.map((brand) => {
        const busy = busyBrandId === brand.id;
        const hasSiblings = brands.length > 1;
        const lockedAsDefault = brand.is_default && hasSiblings;
        return (
          <Box key={brand.id} sx={brandsCardSx}>
            <BrandAvatar brand={brand} />

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Typography sx={brandsCardNameSx}>{brand.brand_name}</Typography>
                {brand.is_default ? (
                  <Chip label="Principal" size="small" sx={brandsDefaultChipSx} />
                ) : null}
              </Box>
              <Typography sx={brandsCardMetaSx}>{buildBrandMeta(brand)}</Typography>
            </Box>

            {busy ? <CircularProgress size={18} sx={{ color: colors.primaryLight }} /> : null}

            {brand.can_manage && !busy ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                {!brand.is_default ? (
                  <Tooltip title="Marcar como principal" arrow slotProps={brandsTooltipSlotProps}>
                    <IconButton
                      size="small"
                      onClick={() => onSetDefault(brand)}
                      aria-label={`Marcar ${brand.brand_name} como principal`}
                      sx={brandsIconButtonSx}
                    >
                      <StarOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title="Editar" arrow slotProps={brandsTooltipSlotProps}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(brand)}
                    aria-label={`Editar ${brand.brand_name}`}
                    sx={brandsIconButtonSx}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={lockedAsDefault
                    ? 'Para eliminarla, marcá otra como principal'
                    : 'Eliminar'}
                  arrow
                  slotProps={brandsTooltipSlotProps}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(brand)}
                      disabled={lockedAsDefault}
                      aria-label={`Eliminar ${brand.brand_name}`}
                      sx={{
                        ...brandsIconButtonSx,
                        '&:hover': { color: brandsTextColors.danger, bgcolor: 'rgba(238,87,87,0.12)' },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
